import BusinessIcon from '@mui/icons-material/Business'
import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'

import theme from 'src/theme'
import React, { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import axios, { pluckData } from 'src/utilities/http-client'
import { CancelIcon, SearchIcon, SubmitIcon } from 'src/v3/core/icons'
import { LinkedService } from 'src/v3/core/types'
import {
  fetchUsers,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectLinkedServiceById,
  selectAccessLevelsStatus,
  fetchAccessLevels,
  selectAccessLevelLinkedServicesStatus,
  fetchAccessLevelLinkedServices,
  fetchLinkedServices,
  selectLinkedServiceRosterStatusByID,
  fetchLinkedServiceRoster,
  selectSelectedLinkedService,
  selectErrorMessage,
  selectedLinkedServiceChanged,
  errorMessageChanged,
  selectSuccessMessage,
  successMessageChanged,
} from '../admin.slice'
import Subheader from '../../components/subheader'
import AccessLevel from './access-level'
import BasicInfo from './basic-info'
import Status from './status'
import CredentialVerification from './credential-verification'
import UserCap from './user-cap'
import BulkUploadUsers from './bulk-upload-users'
import SSO from './sso'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let [open, setOpen] = useState(false)
  let selectedLinkedService = useAppSelector(selectSelectedLinkedService)
  let errorMessage = useAppSelector(selectErrorMessage)
  let successMessage = useAppSelector(selectSuccessMessage)
  let linkedServices = useAppSelector(selectLinkedServices)
  let [searchValue, setSearchValue] = useState<string>('')
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let linkedService = useAppSelector(
    selectLinkedServiceById(selectedLinkedService),
  )
  let accessLevelsStatus = useAppSelector(selectAccessLevelsStatus)
  let accessLevelLinkedServicesStatus = useAppSelector(
    selectAccessLevelLinkedServicesStatus,
  )
  let linkedServiceRosterStatus = useAppSelector(
    selectLinkedServiceRosterStatusByID(selectedLinkedService),
  )

  let [updating, setUpdating] = useState<boolean>(false)
  let [formOpen, setFormOpen] = useState<string>('')
  let [formSaving, setFormSaving] = useState<boolean>(false)
  let [createName, setCreateName] = useState<string>('')

  useEffect(() => {
    if (accessLevelsStatus === 'uninitialized') {
      dispatch(fetchAccessLevels())
    }
  }, [accessLevelsStatus])

  useEffect(() => {
    if (
      selectedLinkedService &&
      (!linkedServiceRosterStatus ||
        linkedServiceRosterStatus === 'uninitialized')
    ) {
      dispatch(fetchLinkedServiceRoster(selectedLinkedService))
    }
  }, [selectedLinkedService, linkedServiceRosterStatus])

  useEffect(() => {
    if (accessLevelLinkedServicesStatus === 'uninitialized') {
      dispatch(fetchAccessLevelLinkedServices())
    }
  }, [accessLevelLinkedServicesStatus])

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  let onLinkedServiceCreated = () => {
    setFormSaving(true)
    axios
      .post<LinkedService>('/api/v2/admin/linked-service', {
        name: createName,
      })
      .then(pluckData)
      .then((data) => dispatch(selectedLinkedServiceChanged(data.id)))
      .then(() => dispatch(fetchLinkedServices()))
      .catch((err) => {
        setFormSaving(false)
        dispatch(
          errorMessageChanged(
            err?.response?.data ||
              err?.message ||
              'Error Creating Linked Service',
          ),
        )
        dispatch(fetchLinkedServices())
      })
      .then(() => setCreateName(''))
      .then(() => setFormSaving(false))
      .then(() => setFormOpen(''))
  }

  return (
    <>
      <div className="flex items-center justify-center mt-2 mb-4">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <BusinessIcon />
        </Avatar>
        <Typography
          variant="h4"
          className="pl-2"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Linked Services
        </Typography>
      </div>
      <Container maxWidth="xl">
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <Button onClick={() => setFormOpen('create')} color="primary">
            Create Linked Service
          </Button>
          <Typography sx={{ mb: '2px' }}>
            or search existing linked services:
          </Typography>
        </Box>
        <Autocomplete
          open={open}
          onOpen={() => {
            setOpen(true)
          }}
          onClose={() => {
            setOpen(false)
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `${option.name} | ${option.id}`}
          onInputChange={(_, value) =>
            dispatch(selectedLinkedServiceChanged(value.split(' | ')[1]))
          }
          options={linkedServices}
          loading={linkedServicesStatus === 'pending'}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(e) => setSearchValue(e.target.value)}
              label="Linked Service Name"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {
                      <SearchIcon
                        sx={{ cursor: 'pointer' }}
                        onClick={() => dispatch(fetchUsers(searchValue))}
                      />
                    }
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {linkedService && (
          <>
            <Subheader title="Linked Service Profile" />
            <Box className="grid grid-cols-12 gap-4">
              <Box className="grid col-span-3">
                <BasicInfo />
              </Box>
              <Box className="grid col-span-9">
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Box className="grid gap-4 lg:grid-cols-4 md:grid-cols-1">
                    <Box>
                      <Status setFormOpen={setFormOpen} formOpen={formOpen} />
                      <CredentialVerification
                        setFormOpen={setFormOpen}
                        formOpen={formOpen}
                      />
                      <SSO />
                    </Box>
                    <AccessLevel />
                    <UserCap />
                    <BulkUploadUsers />
                  </Box>
                </Paper>
              </Box>
            </Box>
          </>
        )}
      </Container>
      <Dialog open={formOpen === 'create'} onClose={() => setFormOpen('')}>
        <Paper sx={{ width: 300, px: 2, py: 1 }}>
          <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
            Create Linked Service
          </DialogTitle>
          <DialogContent
            sx={{
              px: 2,
              pb: 2,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <TextField
              sx={{ mt: 1 }}
              variant="outlined"
              size="small"
              label="Name"
              type="text"
              fullWidth
              value={createName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCreateName(e.target.value)
              }
            />
          </DialogContent>
          <DialogActions
            sx={{ display: 'flex', justifyContent: 'center', paddingBottom: 3 }}
          >
            {formSaving ? (
              <CircularProgress size={36} />
            ) : (
              <>
                <Button
                  onClick={onLinkedServiceCreated}
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={!createName}
                >
                  Submit
                </Button>
                <Button
                  onClick={() => setFormOpen('')}
                  color="error"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
              </>
            )}
          </DialogActions>
        </Paper>
      </Dialog>
      <Backdrop open={updating} onClick={() => setUpdating(false)}>
        <CircularProgress />
      </Backdrop>
      <Snackbar
        open={!!successMessage}
        onClose={() => dispatch(successMessageChanged(''))}
      >
        <Alert
          variant="filled"
          onClose={() => dispatch(successMessageChanged(''))}
          severity="success"
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        onClose={() => dispatch(errorMessageChanged(''))}
      >
        <Alert
          variant="filled"
          onClose={() => dispatch(errorMessageChanged(''))}
          severity="error"
        >
          <strong>Error with request: </strong>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
